import { useEffect, useRef, useState } from "react";
import { FiEye } from "react-icons/fi";
import { toast } from "react-toastify";

import { ModalShell, PrimaryButton, OutlineButton, ServiceTicketToggle } from "src/Pages/WO/Components/upload/uploadModalUI";
import { generateFormPdf } from "src/Pages/WO/Components/forms/formPdf";
import CompanyService from "src/Services/API/company.service";
import { getPOReceiptUrl, getRealFileName, getUploadUrl } from "src/Common/common";
import PoDocument from "./PoDocument";

// Pull an attachment down and inline it as a data URI.
//
// The capture rasterises the DOM through a canvas, and a cross-origin image
// either taints that canvas or fails outright under crossOrigin="anonymous",
// depending on whether the file host returns CORS headers — which is why a PO
// with attachments could fail to generate while the same PO without them was
// fine. A data URI is same-origin by definition, so neither can happen, and
// html-to-image has nothing left to fetch.
//
// Anything that cannot be fetched, or is not an image (e.g. a PDF receipt), is
// dropped rather than failing the whole document.
const toInlineImage = async (attachment) => {
  const fileName = attachment?.fileName || attachment?.name || attachment;
  if (!fileName) return null;

  // A PO's receipts live under poreceipt/ — the same path the detail page's
  // download uses. uploads/ is kept as a fallback because that is where a PO
  // *item*'s attachment goes.
  const candidates = [getPOReceiptUrl(fileName), getUploadUrl(fileName)];
  let lastReason = "no candidate URL";

  for (const url of candidates) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        lastReason = `HTTP ${response.status} at ${url}`;
        continue;
      }
      const blob = await response.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Decide by decoding, not by Content-Type: file hosts routinely serve
      // uploads as application/octet-stream, and rejecting on the header alone
      // silently drops perfectly good photos. Whatever actually decodes is an
      // image; a PDF or anything else fails here and is skipped.
      await new Promise((resolve, reject) => {
        const probe = new Image();
        probe.onload = resolve;
        probe.onerror = () => reject(new Error("not a decodable image"));
        probe.src = dataUrl;
      });

      return { name: getRealFileName(fileName) || fileName, dataUrl };
    } catch (error) {
      // A network/CORS failure lands here too — worth surfacing, since it means
      // the file host is not letting the app read the bytes at all.
      lastReason = `${error?.message || error} at ${url}`;
    }
  }

  console.log(`PO attachment skipped (${lastReason})`);
  return null;
};

/**
 * Preview / Download PDF for a purchase order.
 *
 * The document is rendered as DOM and rasterised client-side, so Preview and
 * Download come from the very same node — what is previewed is exactly what is
 * downloaded. Until previewed the node is parked off-screen (it must stay laid
 * out for the capture to measure it, so it cannot be `display: none`).
 *
 * The two toggles drive the document directly rather than being passed to a
 * server, so they take effect in both preview and download.
 */
const PreviewDownloadPdfModal = ({ shown, order, onClose }) => {
  const [showVendorName, setShowVendorName] = useState(true);
  const [showAttachments, setShowAttachments] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  // The letterhead block under the logo. Failing is not fatal — the document
  // simply omits the address.
  const [company, setCompany] = useState(null);
  // Attachments inlined as data URIs; see toInlineImage above.
  const [inlineAttachments, setInlineAttachments] = useState([]);
  const documentRef = useRef(null);

  useEffect(() => {
    if (!shown || company) return;
    CompanyService.get()
      .then((response) => setCompany(response.data || null))
      .catch((error) => console.log("failed to load company for PO document: ", error));
  }, [shown, company]);

  useEffect(() => {
    if (!shown) return undefined;
    let cancelled = false;
    Promise.all((order?.attachments || []).map(toInlineImage)).then((results) => {
      if (!cancelled) setInlineAttachments(results.filter(Boolean));
    });
    return () => {
      cancelled = true;
    };
  }, [shown, order]);

  if (!shown) return null;

  // The PO records which of the two it is. Not inferred from the rows: both
  // kinds carry line items — the tech app's POs are simple and always have
  // them — so their presence says nothing about the layout.
  const variant = order?.mode === "detailed" ? "detailed" : "simple";

  const download = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const node = documentRef.current;
      // Guard the capture's own preconditions so a failure says which one broke
      // instead of surfacing as an opaque "try again".
      if (!node) throw new Error("document not mounted");
      if (!node.getBoundingClientRect().width) throw new Error("document has no width");

      const pdf = await generateFormPdf(node);
      pdf.save(`PO_${order?.number || "document"}.pdf`);
    } catch (error) {
      console.error("PO pdf generation failed: ", error);
      toast(`Could not generate the PDF: ${error?.message || "unknown error"}`);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <ModalShell
      title="Preview/Download PDF"
      onClose={onClose}
      width={previewing ? "w-[960px]" : "w-[520px]"}
      footer={
        <>
          <OutlineButton onClick={() => setPreviewing((prev) => !prev)} tour="po-pdf-preview">
            <span className="flex items-center gap-2">
              <FiEye size={16} />
              {previewing ? "Hide Preview" : "Preview PDF"}
            </span>
          </OutlineButton>
          <PrimaryButton onClick={download} loading={isDownloading} loadingLabel="Preparing..." tour="po-pdf-download">
            Download PDF
          </PrimaryButton>
        </>
      }
    >
      <div className="px-6 py-5 flex flex-col gap-4" data-tour="po-pdf-toggles">
        <ServiceTicketToggle
          checked={showVendorName}
          onChange={() => setShowVendorName((prev) => !prev)}
          label="Show Vendor Name on PDF"
        />
        <ServiceTicketToggle
          checked={showAttachments}
          onChange={() => setShowAttachments((prev) => !prev)}
          label="Show Attachments on PDF"
        />
      </div>

      {/* ONE instance, always mounted — the container moves rather than the
          document being remounted. Two conditionally-rendered copies sharing a
          single ref can leave it null across the swap, which fails the capture.
          Off-screen (not display:none) while hidden, because the capture has to
          measure a laid-out node.

          Rendered 1:1 — do NOT scale this with a CSS transform. The capture
          sizes itself from getBoundingClientRect(), which counts ancestor
          transforms, so a scaled wrapper crops the right edge off the page. The
          modal is widened instead so the document fits unscaled. */}
      <div className={previewing ? "px-6 pb-6" : undefined}>
        <div
          className={previewing ? "border border-[#E5E8F3] rounded-[8px] bg-[#F7F9FB] p-4 overflow-auto max-h-[52vh]" : undefined}
          style={previewing ? undefined : { position: "fixed", left: -10000, top: 0, pointerEvents: "none" }}
          aria-hidden={!previewing}
        >
          <div style={{ width: 850 }}>
            <PoDocument
              ref={documentRef}
              order={order}
              company={company}
              attachments={inlineAttachments}
              variant={variant}
              showVendorName={showVendorName}
              showAttachments={showAttachments}
            />
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default PreviewDownloadPdfModal;
